// Basic
import PropTypes from 'prop-types';
// Components
import Icon from '@/common-utils/components/renders/icon/Icon';
// Common
import customizationFields from '../../fields/customizationFields';
import isOptionChecked from '../../utils/isOptionChecked';
import { getTextColor } from '@/common-utils/functions/getTextColorBasedOnBackground';
import ICONS from '@/common-utils/enums/icons';
import previewLanguage from '@/common-utils/functions/previewLanguage';

/**
 * Lightweight Android-like payment preview
 *
 * @param {object} props
 * @param {string} props.color - background color (#RRGGBB)
 * @param {string} props.logo - logo url/base64
 * @param {object} props.locale - locale object (key: value)
 * @param {boolean} props.isScreenSaver - renders only logo on color background
 * @param {array} props.checkedOptions - array of checked options
 * @returns {JSX.Element}
 */
function PosTerminalPreview({
    color = '#FFFFFF',
    logo = '',
    locale = 'fr-FR',
    isScreenSaver = false,
    checkedOptions = []
}) {
    const textColor = getTextColor(color || '#FFFFFF');
    const [flagImg, ctaText, otherAmountLabel] = previewLanguage(locale.value);
    const donationPresets = [1, 5, 10];

    const shellClass =
        'relative w-[260px] h-[460px] rounded-[26px] bg-gradient-to-b from-gray-100 to-gray-200 border border-gray-300 shadow-[0_10px_24px_rgba(0,0,0,0.18)] p-2';
    const screenClass =
        'w-full h-full rounded-2xl border border-gray-200 shadow-inner overflow-hidden bg-white flex flex-col';

    const TopBar = () => (
        <div className='flex items-center justify-between px-3 py-2' style={{ backgroundColor: color, color: textColor }}>
            <div className='w-8 h-8 rounded-full bg-white/30 flex items-center justify-center text-xs font-semibold' style={{ color: textColor }}>
                PR
            </div>
            {
                logo?.length > 0 &&
                <img src={logo} alt='Logo' className='h-6 object-contain' />
            }
            <Icon icon={ICONS.MENU} className='text-lg' style={{ color: textColor }} />
        </div>
    );

    if (isScreenSaver) {
        return (
            <div className={shellClass}>
                <div className={`${screenClass} items-center justify-center`} style={{ backgroundColor: color }}>
                    {logo?.length > 0 &&
                        <img src={logo} alt='Logo' className='max-h-24 max-w-[120px] object-contain' />
                    }
                </div>
            </div>
        );
    }

    return (
        <div className={shellClass}>
            <div className={screenClass}>
                <TopBar />
                {isOptionChecked(checkedOptions, customizationFields.MPF_OPTIONS.DONATIONS_MODE.key) ? (
                    <div className='flex-1 flex flex-col px-4 py-5 pb-6 gap-5'>
                        <div className='flex flex-col gap-4 items-center'>
                            {donationPresets.map((amount) => (
                                <button
                                    key={amount}
                                    type='button'
                                    className='w-1/2 rounded-xl py-3 text-xl font-semibold shadow-sm transition-transform'
                                    style={{ backgroundColor: color, color: textColor }}
                                >
                                    {`${amount}€`}
                                </button>
                            ))}

                            <button
                                type='button'
                                className='w-3/4 px-0.5 rounded-xl py-3 text-base font-semibold text-gray-800 bg-gray-300/90 shadow-sm'
                            >
                                {otherAmountLabel}
                            </button>
                        </div>

                        <button
                            type='button'
                            className='mt-auto h-11 w-full rounded-xl font-semibold text-sm tracking-wide shadow-md'
                            style={{ backgroundColor: color, color: textColor }}
                        >
                            {`${ctaText} 1,00 €`}
                        </button>
                    </div>
                ) : (
                    <div className='flex-1 flex items-center justify-center p-4'>
                        <img src={flagImg} alt='Lang preview' className='h-full object-contain' />
                    </div>
                )}
            </div>
        </div>
    );
}

PosTerminalPreview.propTypes = {
    color: PropTypes.string,
    logo: PropTypes.string,
    locale: PropTypes.object,
    isScreenSaver: PropTypes.bool,
    checkedOptions: PropTypes.array
};

export default PosTerminalPreview;


